import { useState, useEffect } from "react";
import { buildCharacterFrames } from "@/utils/buildCharacterFrames";

const PHASE_ONE_FADE_START_DELAY = 3500;
const PHASE_TWO_START_DELAY = 4000;
const BALLOON_START_DELAY = 12500;
const PHASE_THREE_START_DELAY = 19600;
const SPLASH_COMPLETE_DELAY = 20400;
const BALLOON_ASCENT_DURATION = 6000;
const BALLOON_TARGET_ALTITUDE = 40000;
const SPLASH_HANDWRITING = buildCharacterFrames("True Discovery");
const ALTITUDE_FORMATTER = new Intl.NumberFormat("en-US");

const scheduleSplashPhases = ({ onComplete, setPhase, setShowBalloon }) => [
  window.setTimeout(() => setPhase(0), PHASE_ONE_FADE_START_DELAY),
  window.setTimeout(() => setPhase(2), PHASE_TWO_START_DELAY),
  window.setTimeout(() => setShowBalloon(true), BALLOON_START_DELAY),
  window.setTimeout(() => setPhase(3), PHASE_THREE_START_DELAY),
  window.setTimeout(() => onComplete(), SPLASH_COMPLETE_DELAY),
];

const formatAltitude = (altitude) => `${ALTITUDE_FORMATTER.format(altitude)} ft`;

const getPhaseTwoClass = (phase) => {
  if (phase === 2) {
    return "phase-active";
  }

  if (phase > 2) {
    return "phase-done";
  }

  return "phase-waiting";
};

const getPhaseOneClass = (phase) => (phase === 1 ? "phase-active" : "phase-done");

const BalloonIllustration = () => (
  <svg viewBox="0 0 80 130" className="balloon-svg">
    <ellipse cx="40" cy="35" rx="28" ry="35" fill="#c0392b" />
    <ellipse cx="40" cy="35" rx="28" ry="35" fill="url(#balloonShade)" />
    <path d="M28,62 Q34,72 40,75 Q46,72 52,62" fill="#c0392b" />
    <line x1="30" y1="68" x2="33" y2="100" stroke="#2c3e50" strokeWidth="0.8" />
    <line x1="50" y1="68" x2="47" y2="100" stroke="#2c3e50" strokeWidth="0.8" />
    <line x1="40" y1="75" x2="40" y2="100" stroke="#2c3e50" strokeWidth="0.8" />
    <rect x="31" y="100" width="18" height="12" rx="2" fill="#8B6914" stroke="#5C4410" strokeWidth="0.5" />
    <defs>
      <linearGradient id="balloonShade" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(0,0,0,0.15)" />
        <stop offset="40%" stopColor="rgba(255,255,255,0.1)" />
        <stop offset="100%" stopColor="rgba(0,0,0,0.2)" />
      </linearGradient>
    </defs>
  </svg>
);

const SplashHandwriting = ({ phase }) => (
  <div className={`splash-layer ${getPhaseOneClass(phase)}`} data-testid="splash-phase-1">
    <div className="splash-handwriting" data-testid="splash-handwriting">
      {SPLASH_HANDWRITING.map((frame, index) => (
        <span key={frame.id} className="splash-pen-char" style={{ animationDelay: `${index * 0.2}s` }}>
          {frame.char === ' ' ? '\u00A0' : frame.char}
        </span>
      ))}
    </div>
  </div>
);

const SplashCompassAndText = ({ altitude, phase, showBalloon }) => (
  <div className={`splash-layer splash-ct-layer ${getPhaseTwoClass(phase)}`} data-testid="splash-phase-2">
    <div className="compass-mover" data-testid="compass-mover">
      <svg viewBox="0 0 200 200" className="splash-compass-svg">
        <circle cx="100" cy="100" r="80" fill="none" stroke="#1a1a1a" strokeWidth="1.5" className="compass-outer-ring" />
        <circle cx="100" cy="100" r="3" fill="#1a1a1a" />
        <text x="100" y="30" textAnchor="middle" className="compass-dir">N</text>
        <text x="100" y="185" textAnchor="middle" className="compass-dir">S</text>
        <text x="180" y="106" textAnchor="middle" className="compass-dir">E</text>
        <text x="20" y="106" textAnchor="middle" className="compass-dir">W</text>
        <polygon points="100,22 96,100 104,100" fill="#c0392b" className="splash-needle-n" />
        <polygon points="100,178 96,100 104,100" fill="#2c3e50" className="splash-needle-s" />
      </svg>
    </div>
    <div className="typhoon-mover" data-testid="typhoon-mover">
      <svg viewBox="0 0 100 100" className="typhoon-swirl-svg">
        <path
          className="typhoon-swirl-path"
          d="M50,12 C72,12 88,28 88,50 C88,72 72,88 50,88 C33,88 20,78 20,60 C20,44 33,34 48,34 C59,34 66,41 66,50 C66,57 60,62 53,62"
          fill="none"
          stroke="#0055cc"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
    <div className="economy-reveal" data-testid="economy-reveal">
      <div className="economy-stack">
        <span className="eco-word eco-small">The</span>
        <span className="eco-word eco-hero">Economy</span>
        <span className="eco-word eco-small">Has Been</span>
        <span className="eco-word eco-hero eco-punch">Reset</span>
      </div>
    </div>
    <div className={`balloon-flyer ${showBalloon ? 'balloon-ascending' : ''}`} data-testid="balloon-flyer">
      <BalloonIllustration />
      {showBalloon && (
        <span className="altitude-label" data-testid="altitude-label">
          {formatAltitude(altitude)}
        </span>
      )}
    </div>
  </div>
);

const useSplashPhases = ({ onComplete, setPhase, setShowBalloon }) => {
  useEffect(() => {
    const timers = scheduleSplashPhases({ onComplete, setPhase, setShowBalloon });
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [
    BALLOON_START_DELAY,
    PHASE_ONE_FADE_START_DELAY,
    PHASE_THREE_START_DELAY,
    PHASE_TWO_START_DELAY,
    SPLASH_COMPLETE_DELAY,
    onComplete,
    setPhase,
    setShowBalloon,
  ]);
};

const useBalloonAltitude = (showBalloon) => {
  const [altitude, setAltitude] = useState(0);

  useEffect(() => {
    if (!showBalloon) {
      setAltitude(0);
      return undefined;
    }

    let frameId;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / BALLOON_ASCENT_DURATION, 1);

      if (progress >= 1) {
        setAltitude(BALLOON_TARGET_ALTITUDE);
        return;
      }

      setAltitude(Math.round(progress * BALLOON_TARGET_ALTITUDE));
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [BALLOON_ASCENT_DURATION, BALLOON_TARGET_ALTITUDE, setAltitude, showBalloon]);

  return altitude;
};

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(1);
  const [showBalloon, setShowBalloon] = useState(false);
  const altitude = useBalloonAltitude(showBalloon);

  useSplashPhases({ onComplete, setPhase, setShowBalloon });

  return (
    <div className="splash-screen" data-testid="splash-screen">
      <SplashHandwriting phase={phase} />
      <SplashCompassAndText altitude={altitude} phase={phase} showBalloon={showBalloon} />

      <div className={`splash-fade-overlay ${phase >= 3 ? 'fade-active' : ''}`} data-testid="splash-phase-3"></div>
    </div>
  );
};

export default SplashScreen;
